/**
 * Custom Views (Saved Filters) — REAL backend-connected version.
 * URL: /training/custom-views-prototype  (kept for sidebar continuity)
 *
 * Features:
 *  - Real students fetched from /api/training/custom-views/preview & /:id/students
 *  - Real batches/courses for picklist options via /api/training/custom-views/metadata
 *  - View CRUD via /api/training/custom-views (Mongo-backed)
 *  - Cross-field % operators (server-side filter execution)
 *  - Inline drilldown: Zoho Customer + Invoice links + invoices popover
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ChevronDown, ChevronUp, Plus, Star, Search, Filter, Trash2, Pencil,
  Lock, Globe, Users, X, Check, GripVertical, Copy, Eye, Info, Loader2, FileText, ExternalLink, Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import { ZohoCustomerLink, ZohoInvoiceLink } from '../components/ZohoLinks';

const OPERATORS_BY_TYPE = {
  text:     ['is', "isn't", 'contains', "doesn't contain", 'starts with', 'ends with', 'is empty', 'is not empty'],
  number:   ['=', '!=', '>', '<', '>=', '<=', 'between', 'is < % of', 'is ≤ % of', 'is > % of', 'is ≥ % of', 'is = % of', 'is between % of', 'is empty', 'is not empty'],
  date:     ['is', 'before', 'after', 'between', 'is empty', 'is not empty'],
  picklist: ['is', "isn't", 'is any of', 'is none of', 'is empty', 'is not empty'],
};
const CROSS_FIELD_OPS = ['is < % of', 'is ≤ % of', 'is > % of', 'is ≥ % of', 'is = % of', 'is between % of'];

const DEFAULT_COLUMNS = ['enrollment_no', 'name', 'phone', 'course_name', 'total_amount', 'paid_amount', 'due_amount'];

const fmtCurrency = (v) =>
  v == null || v === '' || isNaN(Number(v)) ? '-' :
  Number(v).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });

const visIcon = (v) => v === 'private' ? <Lock className="h-3 w-3" /> : v === 'public' ? <Globe className="h-3 w-3" /> : <Users className="h-3 w-3" />;
const visLabel = (v) => v === 'private' ? 'Private' : v === 'public' ? 'Public' : 'Shared';

const newCriterion = () => ({ id: 'c' + Date.now() + Math.random().toString(36).slice(2, 6), field: 'name', op: 'is', value: '' });

/* =============================================================================
 * MAIN PAGE
 * =============================================================================*/
export default function CustomViewsPage() {
  const [fields, setFields] = useState([]);
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingView, setEditingView] = useState(null);

  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [loadingResult, setLoadingResult] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fieldByKey = useMemo(() => Object.fromEntries(fields.map(f => [f.key, f])), [fields]);
  const numberFields = useMemo(() => fields.filter(f => f.type === 'number'), [fields]);
  const activeView = views.find(v => v.id === activeViewId);

  // ─── load metadata + views ──────────────────────────────────────────
  const loadMetadata = useCallback(async () => {
    try {
      const r = await api.get('/training/custom-views/metadata');
      setFields(r.data.fields || []);
    } catch (e) { toast.error('Failed to load fields'); }
  }, []);

  const loadViews = useCallback(async () => {
    try {
      const r = await api.get('/training/custom-views?module=students');
      const list = r.data.views || [];
      setViews(list);
      // pick first favorite or first view as active
      if (!activeViewId && list.length) {
        const fav = list.find(v => v.favorite);
        setActiveViewId((fav || list[0]).id);
      }
    } catch (e) { toast.error('Failed to load saved views'); }
  }, [activeViewId]);

  useEffect(() => { loadMetadata(); loadViews(); }, [loadMetadata, loadViews]);

  // ─── load students for active view ──────────────────────────────────
  const loadStudents = useCallback(async () => {
    if (!activeView) { setStudents([]); setTotal(0); return; }
    setLoadingResult(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (search) params.append('search', search);
      const r = await api.get(`/training/custom-views/${activeView.id}/students?${params}`);
      setStudents(r.data.students || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error('Failed to load students');
      setStudents([]); setTotal(0);
    } finally { setLoadingResult(false); }
  }, [activeView, page, pageSize, search]);

  useEffect(() => { loadStudents(); }, [loadStudents]);
  useEffect(() => { setPage(1); setExpandedId(null); }, [activeViewId, search]);

  // ─── view actions ───────────────────────────────────────────────────
  const openNewView = () => {
    setEditingView({
      id: null, name: '', visibility: 'private', match: 'AND',
      criteria: [newCriterion()], columns: [...DEFAULT_COLUMNS],
    });
    setBuilderOpen(true);
    setPickerOpen(false);
  };

  const openEditView = (v) => {
    if (!v) return;
    setEditingView({
      id: v.id, name: v.name, visibility: v.visibility, match: v.match,
      criteria: (v.criteria || []).map(c => ({ ...c, id: c.id || ('c' + Math.random()) })),
      columns: v.columns?.length ? [...v.columns] : [...DEFAULT_COLUMNS],
    });
    setBuilderOpen(true);
    setPickerOpen(false);
  };

  const cloneView = (v) => {
    setEditingView({
      id: null, name: `${v.name} (Copy)`, visibility: 'private', match: v.match,
      criteria: (v.criteria || []).map(c => ({ ...c, id: 'c' + Math.random() })),
      columns: [...(v.columns || DEFAULT_COLUMNS)],
    });
    setBuilderOpen(true);
    setPickerOpen(false);
  };

  const deleteView = async (v) => {
    if (!window.confirm(`Delete view "${v.name}"?`)) return;
    try {
      await api.delete(`/training/custom-views/${v.id}`);
      toast.success('View deleted');
      if (activeViewId === v.id) setActiveViewId(null);
      loadViews();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const toggleFavorite = async (v) => {
    try {
      await api.post(`/training/custom-views/${v.id}/favorite`);
      loadViews();
    } catch (e) { toast.error('Failed to update favorite'); }
  };

  const saveView = async (vw) => {
    if (!vw.name.trim()) return toast.error('Please enter a view name');
    const payload = {
      module: 'students',
      name: vw.name.trim(),
      criteria: (vw.criteria || []).map(c => ({
        field: c.field, op: c.op,
        value: c.value ?? '', value2: c.value2 ?? '', compareField: c.compareField || null,
      })),
      columns: vw.columns,
      match: vw.match,
      visibility: vw.visibility,
    };
    try {
      if (vw.id) {
        await api.put(`/training/custom-views/${vw.id}`, payload);
        toast.success('View updated');
      } else {
        const r = await api.post('/training/custom-views', payload);
        toast.success('Custom view created');
        setActiveViewId(r.data.view.id);
      }
      setBuilderOpen(false); setEditingView(null);
      loadViews();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  // ─── grouped picker ────────────────────────────────────────────────
  const grouped = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    const filt = (v) => !q || v.name.toLowerCase().includes(q);
    return {
      'Favorites':       views.filter(v => v.favorite && filt(v)),
      'Created by Me':   views.filter(v => v.is_owner && filt(v)),
      'Shared with Me':  views.filter(v => v.visibility === 'shared' && !v.is_owner && filt(v)),
      'Public Views':    views.filter(v => v.visibility === 'public' && filt(v)),
    };
  }, [views, pickerSearch]);

  const visibleCols = activeView?.columns?.length ? activeView.columns : DEFAULT_COLUMNS;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-full bg-slate-50">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between sticky top-0 z-30" data-testid="cv-topbar">
        <div className="flex items-center gap-3 flex-wrap">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Custom Views</span>
          <span className="text-slate-300">/</span>
          <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Module</span>
          <Badge variant="outline" className="border-slate-300 text-slate-700 font-normal">Students</Badge>
          <span className="text-slate-300">/</span>

          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <button data-testid="cv-view-picker-trigger" className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors group">
                {activeView?.favorite && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />}
                <span className="font-semibold text-slate-900 text-base">{activeView?.name || 'Select a view'}</span>
                <ChevronDown className="h-4 w-4 text-slate-500 group-hover:text-slate-700" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[440px] p-0" align="start" data-testid="cv-view-picker">
              <div className="p-3 border-b border-slate-100">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input data-testid="cv-view-search" placeholder="Search views..." value={pickerSearch}
                         onChange={(e) => setPickerSearch(e.target.value)} className="pl-8 h-8 text-sm" />
                </div>
              </div>
              <div className="max-h-[440px] overflow-y-auto">
                {Object.entries(grouped).every(([_, items]) => items.length === 0) && (
                  <div className="px-3 py-8 text-center text-xs text-slate-400">No saved views yet. Create your first one below.</div>
                )}
                {Object.entries(grouped).map(([section, items]) => items.length > 0 && (
                  <div key={section} className="py-1">
                    <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50">{section}</div>
                    {items.map(v => (
                      <div key={v.id} data-testid={`cv-view-row-${v.id}`}
                           className={`group flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-blue-50 ${activeViewId === v.id ? 'bg-blue-50' : ''}`}
                           onClick={() => { setActiveViewId(v.id); setPickerOpen(false); }}>
                        <button data-testid={`cv-fav-${v.id}`} onClick={(e) => { e.stopPropagation(); toggleFavorite(v); }} className="p-0.5">
                          <Star className={`h-3.5 w-3.5 ${v.favorite ? 'fill-amber-400 text-amber-400' : 'text-slate-300 hover:text-amber-400'}`} />
                        </button>
                        <span className="flex-1 text-sm text-slate-800 truncate">{v.name}</span>
                        <span className="flex items-center gap-1 text-[10px] text-slate-500 px-1.5 py-0.5 bg-slate-100 rounded">{visIcon(v.visibility)} {visLabel(v.visibility)}</span>
                        {activeViewId === v.id && <Check className="h-3.5 w-3.5 text-blue-600" />}
                        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 ml-1">
                          {v.is_owner && <button data-testid={`cv-edit-${v.id}`} onClick={(e) => { e.stopPropagation(); openEditView(v); }} className="p-1 hover:bg-white rounded" title="Edit"><Pencil className="h-3 w-3 text-slate-500" /></button>}
                          <button data-testid={`cv-clone-${v.id}`} onClick={(e) => { e.stopPropagation(); cloneView(v); }} className="p-1 hover:bg-white rounded" title="Clone"><Copy className="h-3 w-3 text-slate-500" /></button>
                          {v.is_owner && !v.system && <button data-testid={`cv-delete-${v.id}`} onClick={(e) => { e.stopPropagation(); deleteView(v); }} className="p-1 hover:bg-white rounded" title="Delete"><Trash2 className="h-3 w-3 text-rose-500" /></button>}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-100 p-2">
                <Button data-testid="cv-new-view-btn" onClick={openNewView} className="w-full justify-start gap-2 bg-blue-600 hover:bg-blue-700 text-white" size="sm">
                  <Plus className="h-4 w-4" /> Create Custom View
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
            <Input data-testid="cv-search-rows" placeholder="Search name / phone / enroll..." value={search}
                   onChange={(e) => setSearch(e.target.value)} className="pl-8 h-8 w-64 text-xs" />
          </div>
          {activeView?.is_owner && (
            <Button variant="outline" size="sm" onClick={() => openEditView(activeView)} data-testid="cv-edit-current">
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit View
            </Button>
          )}
          <Button onClick={openNewView} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="cv-create-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New Custom View
          </Button>
        </div>
      </div>

      {/* Active view chip-strip */}
      {activeView && (
        <div className="px-6 py-3 flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="bg-white text-slate-700 border-slate-200 gap-1">{visIcon(activeView.visibility)} {visLabel(activeView.visibility)}</Badge>
          <Badge variant="outline" className="bg-white text-slate-700 border-slate-200">Owner: {activeView.is_owner ? 'You' : (activeView.owner_name || '—')}</Badge>
          {activeView.criteria?.length > 0 ? activeView.criteria.map((c, i) => (
            <React.Fragment key={c.id || i}>
              {i > 0 && <span className="text-xs font-semibold text-slate-500 px-1">{activeView.match}</span>}
              <CriterionChip c={c} fieldByKey={fieldByKey} />
            </React.Fragment>
          )) : <Badge className="bg-slate-100 text-slate-600 border border-slate-200 font-normal">No filters</Badge>}
          <span className="ml-auto text-xs text-slate-500" data-testid="cv-result-count">
            {loadingResult ? 'Loading...' : `${total} record${total !== 1 ? 's' : ''}`}
          </span>
        </div>
      )}

      {/* Result table */}
      <div className="px-6 pb-10">
        {!activeView ? (
          <Card className="p-12 text-center border-dashed">
            <Filter className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 mb-3">Pick or create a Custom View to start filtering students.</p>
            <Button onClick={openNewView} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1.5" /> Create Custom View</Button>
          </Card>
        ) : (
          <Card className="overflow-hidden border-slate-200">
            <div className="overflow-x-auto">
              <Table data-testid="cv-result-table">
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="w-8"></TableHead>
                    {visibleCols.map(col => (
                      <TableHead key={col} className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold whitespace-nowrap">
                        {fieldByKey[col]?.label || col}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingResult ? (
                    <TableRow><TableCell colSpan={visibleCols.length + 1} className="text-center py-12">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
                    </TableCell></TableRow>
                  ) : students.length === 0 ? (
                    <TableRow><TableCell colSpan={visibleCols.length + 1} className="text-center text-slate-500 py-12">
                      No students match this view.
                    </TableCell></TableRow>
                  ) : students.map(s => (
                    <StudentResultRow key={s.id} s={s} cols={visibleCols} fieldByKey={fieldByKey}
                      expanded={expandedId === s.id} onToggle={() => setExpandedId(expandedId === s.id ? null : s.id)} />
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Pagination */}
            {total > pageSize && (
              <div className="flex items-center justify-between px-4 py-2 border-t border-slate-100 text-xs text-slate-600">
                <span>Page {page} of {totalPages} • {total} records</span>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="cv-page-prev">Prev</Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="cv-page-next">Next</Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>

      <ViewBuilderDialog open={builderOpen} onOpenChange={(o) => { setBuilderOpen(o); if (!o) setEditingView(null); }}
        view={editingView} fields={fields} fieldByKey={fieldByKey} numberFields={numberFields} onSave={saveView} />
    </div>
  );
}

/* =============================================================================
 * CRITERION CHIP (top strip)
 * =============================================================================*/
function CriterionChip({ c, fieldByKey }) {
  const f = fieldByKey[c.field];
  const fLabel = f?.label || c.field;
  const valLabel = (val) => {
    if (val == null || val === '') return '—';
    if (f?.type === 'picklist' && Array.isArray(f.options)) {
      const opt = f.options.find(o => (typeof o === 'object' ? o.value : o) === val);
      if (opt) return typeof opt === 'object' ? opt.label : opt;
    }
    return String(val);
  };
  const compLabel = fieldByKey[c.compareField]?.label;
  return (
    <Badge className="bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-100 font-normal">
      {fLabel} <span className="mx-1 text-blue-500">{c.op}</span>
      {c.op === 'is between % of'
        ? <span className="font-semibold">{c.value || '—'}%–{c.value2 || '—'}% of {compLabel || '—'}</span>
        : CROSS_FIELD_OPS.includes(c.op)
        ? <span className="font-semibold">{c.value || '—'}% of {compLabel || '—'}</span>
        : c.op === 'between'
        ? <span className="font-semibold">{valLabel(c.value)}–{valLabel(c.value2)}</span>
        : <span className="font-semibold">{valLabel(c.value)}</span>}
    </Badge>
  );
}

/* =============================================================================
 * STUDENT RESULT ROW (with inline drilldown)
 * =============================================================================*/
function StudentResultRow({ s, cols, fieldByKey, expanded, onToggle }) {
  const noBatch = (s.enrollment_count || 0) === 0;
  return (
    <>
      <TableRow className={`hover:bg-blue-50/30 cursor-pointer ${noBatch ? 'bg-amber-50/40' : ''}`} onClick={onToggle} data-testid={`cv-student-row-${s.id}`}>
        <TableCell className="text-center w-8 p-2">
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
        </TableCell>
        {cols.map(col => (
          <TableCell key={col} className="text-sm whitespace-nowrap">
            <CellValue s={s} col={col} field={fieldByKey[col]} />
          </TableCell>
        ))}
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={cols.length + 1} className="bg-slate-50/60 p-0">
            <StudentDrilldown s={s} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function CellValue({ s, col, field }) {
  if (col === 'name') return <span className="font-medium text-slate-900">{s.name}</span>;
  if (col === 'enrollment_no') return <ZohoCustomerLink enrollmentNo={s.enrollment_no} zohoCustomerId={s.zoho_customer_id} className="text-xs" />;
  if (['total_amount', 'paid_amount', 'due_amount', 'write_off_amount'].includes(col)) {
    const cls = col === 'paid_amount' ? 'text-emerald-700' : col === 'due_amount' ? 'text-rose-600 font-medium' : col === 'write_off_amount' && s[col] > 0 ? 'text-orange-600' : 'text-slate-700';
    return <span className={cls}>{fmtCurrency(s[col])}</span>;
  }
  if (col === 'invoice_count') return s.invoice_count || 0;
  if (col === 'batch_id' || col === 'batch_name' || col === 'batch_number' || col === 'batch_status' || col === 'course_name') {
    const ens = s.enrollments || [];
    if (ens.length === 0) return <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">No batch</Badge>;
    return (
      <div className="flex flex-col gap-0.5">
        {ens.slice(0, 2).map((en, i) => {
          const text = col === 'batch_id' || col === 'batch_name' ? en.batch_name
                     : col === 'batch_number' ? en.batch_number
                     : col === 'batch_status' ? en.batch_status
                     : en.course_name;
          return (
            <span key={i} className="text-[11px] inline-flex items-center gap-1">
              {col === 'batch_status' ? (
                <Badge variant="outline" className={`text-[9px] px-1 py-0 ${
                  en.batch_status === 'Running' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  en.batch_status === 'Completed' ? 'bg-slate-50 text-slate-600 border-slate-200' :
                  en.batch_status === 'On Hold' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  'bg-blue-50 text-blue-700 border-blue-200'}`}>{en.batch_status}</Badge>
              ) : <span className="text-slate-700 truncate max-w-[180px]">{text || '-'}</span>}
            </span>
          );
        })}
        {ens.length > 2 && <span className="text-[10px] text-slate-400">+{ens.length - 2} more</span>}
      </div>
    );
  }
  const v = s[col];
  if (field?.type === 'picklist' && Array.isArray(field.options)) {
    const opt = field.options.find(o => (typeof o === 'object' ? o.value : o) === v);
    if (opt) return typeof opt === 'object' ? opt.label : opt;
  }
  return (v !== '' && v != null) ? String(v) : <span className="text-slate-300">—</span>;
}

/* =============================================================================
 * INLINE DRILLDOWN — student details + Zoho links + invoices
 * =============================================================================*/
function StudentDrilldown({ s }) {
  const [invoices, setInvoices] = useState(null);
  const [creditNotes, setCreditNotes] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/training/students/${s.id}/invoices`).then(r => r.data.invoices || []).catch(() => []),
      api.get(`/training/students/${s.id}/credit-notes`).then(r => r.data.credit_notes || []).catch(() => []),
    ]).then(([inv, cn]) => { setInvoices(inv); setCreditNotes(cn); })
      .finally(() => setLoading(false));
  }, [s.id]);

  return (
    <div className="grid grid-cols-3 gap-4 p-4">
      {/* Student info */}
      <Card className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Student</div>
        <div className="grid grid-cols-[110px_1fr] gap-y-1.5 text-xs">
          <span className="text-slate-400">Enrollment:</span>
          <ZohoCustomerLink enrollmentNo={s.enrollment_no} zohoCustomerId={s.zoho_customer_id} className="text-xs" />
          <span className="text-slate-400">Name:</span><span className="text-slate-800 font-medium">{s.name}</span>
          <span className="text-slate-400">Phone:</span><span className="text-slate-700">{s.phone || '—'}</span>
          <span className="text-slate-400">Email:</span><span className="text-slate-700 truncate">{s.email || '—'}</span>
          <span className="text-slate-400">Counsellor:</span><span className="text-slate-700">{s.counsellor_name || '—'}</span>
          <span className="text-slate-400">Source:</span>
          {s.source === 'zoho' ? <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200 w-fit">Zoho Books</Badge> : <span className="text-slate-700">Manual</span>}
        </div>
      </Card>

      {/* Enrolments */}
      <Card className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Batch Enrolments ({s.enrollments?.length || 0})</div>
        {(s.enrollments || []).length === 0 ? (
          <p className="text-xs text-slate-400">No batch assigned</p>
        ) : (
          <div className="space-y-1.5">
            {s.enrollments.map((en, i) => (
              <div key={i} className="flex items-center justify-between gap-2 px-2 py-1.5 bg-slate-50 rounded border border-slate-200">
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-xs font-medium text-slate-800 truncate">#{en.batch_number} {en.batch_name}</span>
                  <span className="text-[10px] text-slate-500 truncate">{en.course_name}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {en.enrollment_type === 'upgrade' && <Badge variant="outline" className="text-[9px] px-1 py-0 bg-purple-50 text-purple-700 border-purple-200">Upgrade</Badge>}
                  <Badge variant="outline" className={`text-[9px] px-1 py-0 ${
                    en.batch_status === 'Running' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    en.batch_status === 'Completed' ? 'bg-slate-50 text-slate-600 border-slate-200' :
                    en.batch_status === 'On Hold' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    'bg-blue-50 text-blue-700 border-blue-200'}`}>{en.batch_status || '-'}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Fees */}
      <Card className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Fees Summary</div>
        <div className="grid grid-cols-2 gap-y-1.5 text-xs">
          <span className="text-slate-400">Total:</span><span className="text-right font-medium text-slate-800">{fmtCurrency(s.total_amount)}</span>
          <span className="text-slate-400">Paid:</span><span className="text-right font-medium text-emerald-700">{fmtCurrency(s.paid_amount)}</span>
          {s.write_off_amount > 0 && (<>
            <span className="text-slate-400">Write-off:</span><span className="text-right font-medium text-orange-600">{fmtCurrency(s.write_off_amount)}</span>
          </>)}
          {s.credit_amount > 0 && (<>
            <span className="text-slate-400">Credit Notes:</span><span className="text-right font-medium text-violet-700">{fmtCurrency(s.credit_amount)}</span>
          </>)}
          <span className="text-slate-400">Net Outstanding:</span><span className="text-right font-medium text-rose-600">{fmtCurrency(s.net_outstanding ?? s.due_amount)}</span>
          <span className="text-slate-400 col-span-1">% Paid:</span>
          <span className="text-right">
            {s.total_amount > 0
              ? <span className={`font-semibold ${s.paid_amount / s.total_amount < 0.3 ? 'text-rose-600' : s.paid_amount / s.total_amount < 0.7 ? 'text-amber-600' : 'text-emerald-700'}`}>
                  {Math.round((s.paid_amount / s.total_amount) * 100)}%
                </span>
              : <span className="text-slate-400">—</span>}
          </span>
        </div>
      </Card>

      {/* Credit Notes */}
      {creditNotes && creditNotes.length > 0 && (
        <Card className="p-3 col-span-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1">
            <Receipt className="h-3 w-3 text-violet-600" /> Credit Notes ({creditNotes.length})
          </div>
          <div className="grid grid-cols-2 gap-3">
            {creditNotes.map((cn) => (
              <Card key={cn.creditnote_id} className="p-2.5 bg-violet-50/40 border-violet-200">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-violet-900">{cn.creditnote_number}</span>
                  <Badge variant="outline" className={`text-[9px] ${cn.status === 'closed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : cn.status === 'open' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>{cn.status}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-1 text-[10px] text-slate-600 mb-2">
                  <span>Date: <b>{cn.date}</b></span>
                  <span>Total: <b className="text-slate-800">{fmtCurrency(cn.total)}</b></span>
                  <span>Applied: <b className="text-emerald-700">{fmtCurrency(cn.credits_applied)}</b></span>
                </div>
                {(cn.invoices_credited || []).length > 0 && (
                  <div className="border-t border-violet-200 pt-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-violet-700 font-semibold mb-1">Applied To</div>
                    {cn.invoices_credited.map((ic, idx) => (
                      <div key={idx} className="flex items-center justify-between text-[10px] py-0.5">
                        <ZohoInvoiceLink invoiceNumber={ic.invoice_number} invoiceId={ic.invoice_id} className="text-[10px]" />
                        <span className="text-emerald-700 font-medium">{fmtCurrency(ic.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </Card>
      )}

      {/* Invoices */}
      <Card className="p-3 col-span-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1">
          <FileText className="h-3 w-3" /> Invoices ({s.invoice_count || 0})
        </div>
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        ) : !invoices || invoices.length === 0 ? (
          <p className="text-xs text-slate-400">No invoice details available</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Invoice #</th>
                  <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date</th>
                  <th className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                  <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total</th>
                  <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Paid</th>
                  <th className="text-right px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Due</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, i) => (
                  <tr key={inv.invoice_id || i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-2 py-1.5"><ZohoInvoiceLink invoiceNumber={inv.invoice_number} invoiceId={inv.invoice_id} className="text-xs" /></td>
                    <td className="px-2 py-1.5 text-slate-600">{inv.date || inv.invoice_date || '—'}</td>
                    <td className="px-2 py-1.5">
                      <Badge variant="outline" className={`text-[9px] ${
                        inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        inv.status === 'partially_paid' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        {inv.status || '—'}
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5 text-right text-slate-700">{fmtCurrency(inv.total)}</td>
                    <td className="px-2 py-1.5 text-right text-emerald-700">{fmtCurrency(inv.paid)}</td>
                    <td className="px-2 py-1.5 text-right text-rose-600">{fmtCurrency(inv.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/* =============================================================================
 * VIEW BUILDER DIALOG
 * =============================================================================*/
function ViewBuilderDialog({ open, onOpenChange, view, fields, fieldByKey, numberFields, onSave }) {
  const [draft, setDraft] = useState(view);
  const [tab, setTab] = useState('criteria');
  const [colSearch, setColSearch] = useState('');

  useEffect(() => { setDraft(view); setTab('criteria'); setColSearch(''); }, [view]);
  if (!draft) return null;

  const update = (patch) => setDraft(prev => ({ ...prev, ...patch }));
  const addCriterion = () => update({ criteria: [...draft.criteria, newCriterion()] });
  const removeCriterion = (id) => update({ criteria: draft.criteria.filter(c => c.id !== id) });
  const updateCriterion = (id, patch) => update({ criteria: draft.criteria.map(c => c.id === id ? { ...c, ...patch } : c) });

  const isSelected = (k) => draft.columns.includes(k);
  const toggleColumn = (k) => update({ columns: isSelected(k) ? draft.columns.filter(c => c !== k) : [...draft.columns, k] });
  const moveColumn = (k, dir) => {
    const idx = draft.columns.indexOf(k); if (idx === -1) return;
    const ni = idx + dir; if (ni < 0 || ni >= draft.columns.length) return;
    const arr = [...draft.columns]; [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    update({ columns: arr });
  };

  const groups = ['Student', 'Fees', 'Batch'];
  const filteredFields = fields.filter(f => !colSearch || f.label.toLowerCase().includes(colSearch.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] flex flex-col p-0" data-testid="cv-builder-dialog">
        <DialogHeader className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-t-lg">
          <DialogTitle className="text-white text-base font-semibold flex items-center gap-2">
            <Filter className="h-4 w-4" /> {draft.id ? 'Edit Custom View' : 'New Custom View'}
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">View Name *</Label>
              <Input data-testid="cv-builder-name" value={draft.name} onChange={e => update({ name: e.target.value })} placeholder="e.g. Defaulters — Paid &lt; 30%" className="mt-1" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">Sharing</Label>
              <Select value={draft.visibility} onValueChange={v => update({ visibility: v })}>
                <SelectTrigger className="mt-1" data-testid="cv-builder-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="private"><span className="flex items-center gap-2"><Lock className="h-3 w-3" /> Private (only me)</span></SelectItem>
                  <SelectItem value="shared"><span className="flex items-center gap-2"><Users className="h-3 w-3" /> Shared with selected users</span></SelectItem>
                  <SelectItem value="public"><span className="flex items-center gap-2"><Globe className="h-3 w-3" /> Public (everyone)</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 overflow-hidden flex flex-col">
          <TabsList className="mx-6 mt-3 bg-slate-100 inline-flex h-9 w-fit">
            <TabsTrigger value="criteria" data-testid="cv-tab-criteria">Criteria</TabsTrigger>
            <TabsTrigger value="columns" data-testid="cv-tab-columns">Columns ({draft.columns.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="criteria" className="flex-1 min-h-0 overflow-y-auto px-6 py-4 mt-0 space-y-3 data-[state=inactive]:hidden">
            <div className="flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
              <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-[11px] text-blue-900 leading-relaxed">
                <span className="font-semibold">Cross-Field % operators</span> let you compare two number fields proportionally.
                <span className="block mt-0.5">Example: <i>Paid Amount</i> <code className="bg-blue-100 px-1 rounded">is &lt; % of</code> <b>30</b>% of <i>Total Amount</i> → finds students who've paid less than 30% of total.</span>
                <span className="block mt-0.5 text-blue-700/80">Batch / Course filters match if <i>any</i> enrolment satisfies the rule.</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-600 font-medium">Match</span>
              <RadioGroup value={draft.match} onValueChange={v => update({ match: v })} className="flex gap-3">
                <div className="flex items-center gap-1.5"><RadioGroupItem value="AND" id="m-and" data-testid="cv-match-and" /><Label htmlFor="m-and" className="text-xs cursor-pointer">All conditions (AND)</Label></div>
                <div className="flex items-center gap-1.5"><RadioGroupItem value="OR" id="m-or" data-testid="cv-match-or" /><Label htmlFor="m-or" className="text-xs cursor-pointer">Any condition (OR)</Label></div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              {draft.criteria.length === 0 && (
                <div className="text-xs text-slate-500 italic py-3 border border-dashed border-slate-300 rounded-md text-center">No conditions — view will show all students.</div>
              )}
              {draft.criteria.map((c, idx) => (
                <CriterionRow key={c.id} index={idx} match={draft.match} criterion={c}
                  fields={fields} fieldByKey={fieldByKey} numberFields={numberFields}
                  onChange={(p) => updateCriterion(c.id, p)} onRemove={() => removeCriterion(c.id)} />
              ))}
            </div>

            <Button data-testid="cv-add-criterion" type="button" variant="outline" size="sm" onClick={addCriterion} className="gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Add Condition
            </Button>
          </TabsContent>

          <TabsContent value="columns" className="flex-1 min-h-0 overflow-hidden mt-0 px-6 py-4 data-[state=inactive]:hidden">
            <div className="grid grid-cols-2 gap-4 h-[55vh] min-h-[400px]">
              <Card className="p-3 overflow-hidden flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-2 flex-shrink-0">
                  <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">Available Fields</div>
                  <span className="text-[10px] text-slate-400">{filteredFields.length} of {fields.length}</span>
                </div>
                <div className="relative mb-2 flex-shrink-0">
                  <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
                  <Input data-testid="cv-col-search" placeholder="Search fields..." value={colSearch} onChange={(e) => setColSearch(e.target.value)} className="pl-8 h-8 text-sm" />
                </div>
                <div className="overflow-y-auto flex-1 min-h-0 space-y-1 pr-1">
                  {groups.map(group => {
                    const items = filteredFields.filter(f => f.group === group);
                    if (items.length === 0) return null;
                    return (
                      <div key={group}>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-1 py-1 mt-1 sticky top-0 bg-white z-10">{group} Fields</div>
                        {items.map(f => (
                          <label key={f.key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer" data-testid={`cv-col-toggle-${f.key}`}>
                            <Checkbox checked={isSelected(f.key)} onCheckedChange={() => toggleColumn(f.key)} />
                            <span className="text-sm text-slate-700">{f.label}</span>
                          </label>
                        ))}
                      </div>
                    );
                  })}
                  {filteredFields.length === 0 && <div className="text-xs text-slate-400 italic text-center py-6">No fields match "{colSearch}"</div>}
                </div>
              </Card>

              <Card className="p-3 overflow-hidden flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-2 flex-shrink-0">
                  <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">Selected Columns ({draft.columns.length})</div>
                  <button onClick={() => update({ columns: [] })} className="text-[11px] text-rose-600 hover:underline" data-testid="cv-col-clear">Clear All</button>
                </div>
                <div className="overflow-y-auto flex-1 min-h-0 space-y-1">
                  {draft.columns.length === 0 && <div className="text-xs text-slate-400 italic text-center py-6">Select columns from the left</div>}
                  {draft.columns.map((k, i) => (
                    <div key={k} className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded border border-slate-200">
                      <GripVertical className="h-3.5 w-3.5 text-slate-400" />
                      <span className="flex-1 text-sm text-slate-800">{fieldByKey[k]?.label || k}</span>
                      <button onClick={() => moveColumn(k, -1)} disabled={i === 0} className="p-0.5 disabled:opacity-30 hover:bg-white rounded"><ChevronDown className="h-3 w-3 rotate-180 text-slate-500" /></button>
                      <button onClick={() => moveColumn(k, 1)} disabled={i === draft.columns.length - 1} className="p-0.5 disabled:opacity-30 hover:bg-white rounded"><ChevronDown className="h-3 w-3 text-slate-500" /></button>
                      <button onClick={() => toggleColumn(k)} className="p-0.5 hover:bg-rose-50 rounded"><X className="h-3 w-3 text-rose-500" /></button>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="px-6 py-3 border-t border-slate-100 bg-slate-50">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cv-builder-cancel">Cancel</Button>
          <Button onClick={() => onSave(draft)} className="bg-blue-600 hover:bg-blue-700" data-testid="cv-builder-save">
            <Eye className="h-4 w-4 mr-1.5" /> {draft.id ? 'Save Changes' : 'Save & Apply View'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* =============================================================================
 * SINGLE CRITERION ROW (in builder)
 * =============================================================================*/
function CriterionRow({ index, match, criterion, fields, fieldByKey, numberFields, onChange, onRemove }) {
  const field = fieldByKey[criterion.field];
  const ops = field ? OPERATORS_BY_TYPE[field.type] : OPERATORS_BY_TYPE.text;
  const showValue = !['is empty', 'is not empty'].includes(criterion.op);
  const groups = ['Student', 'Fees', 'Batch'];

  const renderPicklist = (val, onSel, placeholder, testId) => (
    <Select value={val || ''} onValueChange={onSel}>
      <SelectTrigger className="h-9" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-60">
        {(field.options || []).map(o => {
          const v = typeof o === 'object' ? o.value : o;
          const lab = typeof o === 'object' ? o.label : o;
          return <SelectItem key={v} value={v}>{lab}</SelectItem>;
        })}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-1" data-testid={`cv-criterion-row-${index}`}>
      <div className="flex items-start gap-2">
        <div className="w-12 flex items-center justify-center pt-2 text-[11px] font-semibold text-slate-500">
          {index === 0 ? <span className="text-slate-400">WHERE</span> : <span className="px-2 py-0.5 rounded bg-slate-200 text-slate-700">{match}</span>}
        </div>

        <div className="flex-1">
          <Select value={criterion.field} onValueChange={(v) => {
            const f = fieldByKey[v];
            const defaultOp = OPERATORS_BY_TYPE[f.type][0];
            onChange({ field: v, op: defaultOp, value: '', value2: '', compareField: undefined });
          }}>
            <SelectTrigger className="h-9" data-testid={`cv-cr-field-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {groups.map(g => {
                const items = fields.filter(f => f.group === g);
                if (!items.length) return null;
                return (
                  <div key={g}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-2 pt-2 pb-1">{g} Fields</div>
                    {items.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                  </div>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="w-44">
          <Select value={criterion.op} onValueChange={(v) => onChange({
            op: v,
            compareField: CROSS_FIELD_OPS.includes(v) ? (criterion.compareField || numberFields.find(x => x.key !== criterion.field)?.key) : undefined,
          })}>
            <SelectTrigger className="h-9" data-testid={`cv-cr-op-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {ops.map(op => <SelectItem key={op} value={op}>{op}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-[1.4]">
          {CROSS_FIELD_OPS.includes(criterion.op) ? (
            <div className="flex items-center gap-1.5">
              <Input data-testid={`cv-cr-val-${index}`} type="number" value={criterion.value} onChange={e => onChange({ value: e.target.value })}
                placeholder={criterion.op === 'is between % of' ? 'Min %' : '30'} className="h-9 w-20" />
              {criterion.op === 'is between % of' && (<>
                <span className="text-xs text-slate-400">–</span>
                <Input data-testid={`cv-cr-val2-${index}`} type="number" value={criterion.value2 || ''} onChange={e => onChange({ value2: e.target.value })} placeholder="Max %" className="h-9 w-20" />
              </>)}
              <span className="text-xs text-slate-500 font-medium whitespace-nowrap">% of</span>
              <Select value={criterion.compareField || ''} onValueChange={(v) => onChange({ compareField: v })}>
                <SelectTrigger className="h-9 flex-1" data-testid={`cv-cr-compare-${index}`}><SelectValue placeholder="Select field" /></SelectTrigger>
                <SelectContent>{numberFields.filter(f => f.key !== criterion.field).map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : !showValue ? (
            <Input disabled value="" placeholder="(no value)" className="h-9 bg-slate-50" />
          ) : criterion.op === 'between' ? (
            <div className="flex items-center gap-1.5">
              <Input type={field?.type === 'date' ? 'date' : 'number'} value={criterion.value} onChange={e => onChange({ value: e.target.value })} placeholder="From" className="h-9 flex-1" />
              <span className="text-xs text-slate-400">–</span>
              <Input type={field?.type === 'date' ? 'date' : 'number'} value={criterion.value2 || ''} onChange={e => onChange({ value2: e.target.value })} placeholder="To" className="h-9 flex-1" />
            </div>
          ) : field?.type === 'picklist' ? (
            renderPicklist(criterion.value, v => onChange({ value: v }), 'Select value', `cv-cr-val-${index}`)
          ) : (
            <Input data-testid={`cv-cr-val-${index}`} type={field?.type === 'number' ? 'number' : field?.type === 'date' ? 'date' : 'text'}
              value={criterion.value} onChange={e => onChange({ value: e.target.value })} placeholder={field?.type === 'date' ? '' : 'Enter value'} className="h-9" />
          )}
        </div>

        <Button type="button" variant="ghost" size="icon" onClick={onRemove} className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 h-9 w-9" data-testid={`cv-cr-remove-${index}`}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
